import { useState, useEffect, useContext } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { VoiceContext } from "@/components/voice/GlobalVoiceProvider";
import { GlobalDialer } from "@/components/voice/GlobalDialer";
import { useProductTour } from "@/components/tour";
import { 
  X, 
  Rocket, 
  Check,
  Circle,
  ChevronRight,
  ChevronDown,
  Phone,
  ListTodo,
  Clock,
  Pin,
  ArrowRight,
  PhoneMissed,
  MessageSquare,
  Volume2,
  Clipboard,
  Delete,
  Sparkles,
  BarChart3,
  Users,
  Zap,
  CheckCircle2,
  HelpCircle,
  BookOpen,
  MessageCircle,
  Mail,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";

interface QuickstartItem {
  id: string;
  backendId: string; // Maps to backend API item ID
  title: string;
  description?: string;
  completed: boolean;
  actionLabel?: string;
  actionPath?: string; // Route to navigate to
  skipLabel?: string;
}

interface QuickstartSection {
  title: string;
  icon: React.ElementType;
  items: QuickstartItem[];
}

interface Note {
  id: number;
  userId: string;
  organizationId: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

// Map backend IDs to frontend items
const BACKEND_TO_FRONTEND_MAP: Record<string, string> = {
  'phone_number_added': 'phone-number',
  'first_call_made': 'first-call',
  'first_message_sent': 'first-text',
  'team_member_invited': 'invite-team',
  'business_hours_configured': 'business-hours',
  'ai_voice_configured': 'ai-assistant',
  'auto_reply_configured': 'auto-reply',
  'voicemail_configured': 'voicemail',
};

const utilityPane = {
  canvas: "bg-background",
  rail: "bg-input",
  surface: "bg-card",
  elevated: "bg-surface-elevated",
  elevatedHover: "hover:bg-surface-elevated",
  border: "border-border",
  borderStrong: "border-border-layer-strong",
  text: "text-text-layer-primary",
  textSoft: "text-text-layer-secondary",
  textMuted: "text-text-layer-muted",
};

const utilityPaneTabBase =
  "w-full py-4 flex flex-col items-center justify-center gap-1.5 transition-colors";
const utilityPaneTabIdle = "text-text-layer-muted hover:text-text-layer-primary hover:bg-surface-elevated";
const utilityPaneTabActive = "text-text-layer-primary bg-surface-elevated";

interface DashboardRightSidebarProps {
  onNewSms?: () => void;
  onNewContact?: () => void;
}

export function DashboardRightSidebar({ onNewSms, onNewContact }: DashboardRightSidebarProps = {}) {
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'dialer' | 'tasks' | 'notes' | 'quickstart' | 'help'>('dialer');
  const [currentNote, setCurrentNote] = useState('');
  const [dialerNumber, setDialerNumber] = useState('');
  const [recentNumbers, setRecentNumbers] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Get Started': true,
    'Set Up Your Team': false,
    'Automate Your Workflow': false,
  });
  const [setupDismissed, setSetupDismissed] = useState(() => {
    return localStorage.getItem('limephone_setup_dismissed') === 'true';
  });
  const { toast } = useToast();
  const { startTour, resetTours, tourState } = useProductTour();
  const queryClient = useQueryClient();

  // Fetch notes from API
  const { data: notes = [], isLoading: notesLoading, isError: notesError } = useQuery<Note[]>({
    queryKey: ['/api/notes'],
    refetchOnWindowFocus: false,
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async (content: string) => {
      return apiRequest('POST', '/api/notes', { content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notes'] });
      setCurrentNote('');
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create note', variant: 'destructive' });
    },
  });

  // Update note mutation (for pin toggle)
  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, isPinned }: { id: number; isPinned: boolean }) => {
      return apiRequest('PATCH', `/api/notes/${id}`, { isPinned });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notes'] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update note', variant: 'destructive' });
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest('DELETE', `/api/notes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notes'] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete note', variant: 'destructive' });
    },
  });
  
  const voiceContext = useContext(VoiceContext);
  const makeCall = voiceContext?.makeCall;
  const isDeviceReady = voiceContext?.isDeviceReady || false;
  
  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  const { data: priorityFeed = [] } = useQuery<any[]>({
    queryKey: ['/api/dashboard/personal/priority-feed'],
  });

  // Fetch phone numbers to check if any exist. Use the real org-scoped route —
  // '/api/phone-numbers' has no bare handler (only '/:sid/...' sub-routes), so it
  // returned nothing and hasPhoneNumber was always false for everyone (incl. team
  // members who DO have an org number).
  const { data: phoneNumbers = [] } = useQuery<any[]>({
    queryKey: ['/api/organization/phone-numbers'],
    refetchOnWindowFocus: false,
  });

  const hasPhoneNumber = phoneNumbers.length > 0;

  // Fetch smart progress from API
  const { data: smartProgress } = useQuery<{ completedItems: string[] }>({
    queryKey: ['/api/onboarding/smart-progress'],
    refetchOnWindowFocus: false,
  });

  // State for invite team popup
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  // Invite team member mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      return apiRequest('POST', '/api/organization/invite', data);
    },
    onSuccess: () => {
      toast({ title: 'Invitation sent', description: 'Team member has been invited.' });
      setShowInvitePopup(false);
      setInviteEmail('');
      completeStepMutation.mutate('team_member_invited');
    },
    onError: (error: any) => {
      toast({ title: 'Failed to invite', description: error.message || 'Could not send invitation.', variant: 'destructive' });
    },
  });

  // Mutation to mark items complete
  const completeStepMutation = useMutation({
    mutationFn: async (stepId: string) => {
      const response = await fetch('/api/onboarding/smart-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stepId }),
      });
      if (!response.ok) throw new Error('Failed to complete step');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/onboarding/smart-progress'] });
    },
  });

  // Mutation to skip items
  const skipStepMutation = useMutation({
    mutationFn: async (stepId: string) => {
      const response = await fetch('/api/onboarding/smart-skip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stepId }),
      });
      if (!response.ok) throw new Error('Failed to skip step');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/onboarding/smart-progress'] });
    },
  });

  // Helper to check if an item is completed or skipped
  const isItemCompleteOrSkipped = (backendId: string) => {
    const completedItems = smartProgress?.completedItems ?? [];
    return completedItems.includes(backendId) || completedItems.includes(`skipped_${backendId}`);
  };

  // Build quickstart sections with API data - use direct phone number check for more reliable detection
  const isPhoneNumberComplete = hasPhoneNumber || (smartProgress?.completedItems?.includes('phone_number_added') ?? false);
  
  const quickstartSections: QuickstartSection[] = [
    {
      title: "Get Started",
      icon: Sparkles,
      items: [
        { 
          id: "phone-number", 
          backendId: "phone_number_added",
          title: isPhoneNumberComplete ? "Manage your phone number" : "Get your first phone number", 
          completed: isPhoneNumberComplete,
          actionLabel: isPhoneNumberComplete ? "Manage number" : "Get number",
          actionPath: "/settings?section=phone-numbers"
        },
        { 
          id: "first-call", 
          backendId: "first_call_made",
          title: "Make your first call", 
          completed: isItemCompleteOrSkipped('first_call_made'), 
          actionLabel: "Make a call", 
          description: "Test your phone setup by making a quick call."
        },
        { 
          id: "first-text", 
          backendId: "first_message_sent",
          title: "Send your first text message", 
          completed: isItemCompleteOrSkipped('first_message_sent'), 
          actionLabel: "Send SMS", 
          actionPath: "/inbox",
          description: "Try sending an SMS to see how messaging works." 
        },
      ]
    },
    {
      title: "Set Up Your Team",
      icon: Users,
      items: [
        { 
          id: "invite-team", 
          backendId: "team_member_invited",
          title: "Invite a team member", 
          completed: isItemCompleteOrSkipped('team_member_invited'), 
          actionLabel: "Invite teammate", 
          description: "Invite teammates to collaborate with you.", 
          skipLabel: "Skip" 
        },
        { 
          id: "business-hours", 
          backendId: "business_hours_configured",
          title: "Configure business hours", 
          completed: isItemCompleteOrSkipped('business_hours_configured'), 
          actionLabel: "Configure",
          actionPath: "/settings?section=business-hours",
          skipLabel: "Skip", 
          description: "Set when your team is available to take calls." 
        },
      ]
    },
    {
      title: "Automate Your Workflow",
      icon: Zap,
      items: [
        { 
          id: "ai-assistant", 
          backendId: "ai_voice_configured",
          title: "Enable AI assistant for calls", 
          completed: isItemCompleteOrSkipped('ai_voice_configured'), 
          actionLabel: "Set up", 
          actionPath: "/settings/voice-agents",
          description: "Let AI handle calls when you're busy." 
        },
        { 
          id: "auto-reply", 
          backendId: "auto_reply_configured",
          title: "Set up auto-reply messages", 
          completed: isItemCompleteOrSkipped('auto_reply_configured'), 
          actionLabel: "Configure",
          actionPath: "/settings?section=call-settings",
          skipLabel: "Skip", 
          description: "Automatically respond to messages after hours." 
        },
        { 
          id: "voicemail", 
          backendId: "voicemail_configured",
          title: "Customize your voicemail greeting", 
          completed: isItemCompleteOrSkipped('voicemail_configured'), 
          actionLabel: "Configure",
          actionPath: "/settings?section=call-settings",
          skipLabel: "Skip" 
        },
      ]
    },
  ];

  useEffect(() => {
    const stored = localStorage.getItem('recentDialerNumbers');
    if (stored) {
      try {
        setRecentNumbers(JSON.parse(stored).slice(0, 5));
      } catch {}
    }
  }, []);

  const handleAction = (item: QuickstartItem) => {
    // Special case for making a call - open the dialer
    if (item.id === 'first-call') {
      setActiveTab('dialer');
      return;
    }
    // Special case for invite team - show popup
    if (item.id === 'invite-team') {
      setShowInvitePopup(true);
      return;
    }
    // Navigate to the action path if provided
    if (item.actionPath) {
      setIsOpen(false);
      setLocation(item.actionPath);
    }
  };

  const handleSkip = (item: QuickstartItem) => {
    // Mark as skipped via API
    skipStepMutation.mutate(item.backendId);
  };

  const totalItems = quickstartSections.reduce((acc, s) => acc + s.items.length, 0);
  const completedItems = quickstartSections.reduce((acc, s) => acc + s.items.filter(i => i.completed).length, 0);
  const remainingItems = totalItems - completedItems;
  const progressPercent = Math.round((completedItems / totalItems) * 100);
  const isQuickstartComplete = progressPercent === 100;

  const handleDismissSetup = () => {
    localStorage.setItem('limephone_setup_dismissed', 'true');
    setSetupDismissed(true);
  };

  const handleExploreFeatures = () => {
    setIsOpen(false);
    setLocation('/inbox');
  };

  const getNextIncompleteItems = () => {
    const items: { section: string; item: QuickstartItem }[] = [];
    for (const section of quickstartSections) {
      for (const item of section.items) {
        if (!item.completed && items.length < 2) {
          items.push({ section: section.title, item });
        }
      }
    }
    return items;
  };

  const handleDial = (number: string) => {
    if (!number.trim()) return;
    
    const cleanNumber = number.replace(/\D/g, '');
    if (cleanNumber.length < 10) {
      toast({ title: "Invalid number", description: "Please enter a valid phone number", variant: "destructive" });
      return;
    }

    const updated = [number, ...recentNumbers.filter(n => n !== number)].slice(0, 5);
    setRecentNumbers(updated);
    localStorage.setItem('recentDialerNumbers', JSON.stringify(updated));

    if (makeCall && isDeviceReady) {
      makeCall(number.startsWith('+') ? number : `+1${cleanNumber}`);
    } else {
      toast({ title: "Dialer not ready", description: "Opening full dialer...", variant: "default" });
      window.location.href = '/dialer';
    }
    setDialerNumber('');
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const cleaned = text.replace(/\D/g, '');
      if (cleaned.length >= 10) {
        setDialerNumber(text);
      } else {
        toast({ title: "No valid number", description: "Clipboard doesn't contain a valid phone number", variant: "destructive" });
      }
    } catch {
      toast({ title: "Clipboard access denied", description: "Please paste manually", variant: "destructive" });
    }
  };

  const formatOverdue = (age: string) => {
    const match = age.match(/(\d+)\s*(h|hour|day|d|min|m)/i);
    if (!match) return age;
    const value = parseInt(match[1]);
    const unit = match[2].toLowerCase();
    if (unit.startsWith('h') && value > 24) {
      const days = Math.floor(value / 24);
      return `Overdue · ${days}d`;
    }
    if (unit.startsWith('d') || unit === 'day') {
      return `Overdue · ${value}d`;
    }
    return age;
  };

  const addNote = () => {
    if (!currentNote.trim()) return;
    createNoteMutation.mutate(currentNote.trim());
  };

  const togglePinNote = (id: number) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      updateNoteMutation.mutate({ id, isPinned: !note.isPinned });
    }
  };

  const deleteNote = (id: number) => {
    deleteNoteMutation.mutate(id);
  };

  const convertToTask = (noteId: number) => {
    toast({ title: "Task created", description: "Note converted to a task" });
  };

  const tabs = [
    { id: 'dialer' as const, label: 'Dial', icon: Phone },
    { id: 'help' as const, label: 'Help', icon: HelpCircle },
  ];

  const sortedNotes = [...notes].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const tasks = (priorityFeed || []).slice(0, 5);

  return (
    <>
      {/* Collapsed sidebar - vertical icon tabs */}
      <div 
        className={cn(
          "fixed right-0 top-0 h-full z-40 flex flex-col items-center pt-16 border-l transition-all duration-200",
          utilityPane.rail,
          utilityPane.border,
          isOpen ? "translate-x-full" : "translate-x-0"
        )}
        style={{ width: '56px' }}
        data-tour="right-sidebar"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setIsOpen(true);
            }}
            className={cn(
              utilityPaneTabBase,
              utilityPaneTabIdle,
              activeTab === tab.id && isOpen && utilityPaneTabActive
            )}
            title={tab.label}
          >
            <tab.icon className="h-5 w-5" />
            <span className="text-[9px] font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Expanded sidebar panel with side tabs */}
      <div 
        className={cn(
          "fixed right-0 top-0 h-full z-50 border-l shadow-[0_24px_80px_rgba(0,0,0,0.45)] transition-transform duration-200 ease-out flex",
          utilityPane.canvas,
          utilityPane.border,
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{ width: '400px' }}
      >
        {/* Vertical side tabs */}
        <div className={cn("w-14 border-r flex flex-col pt-16 shrink-0", utilityPane.rail, utilityPane.border)}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                utilityPaneTabBase,
                "border-l-2",
                activeTab === tab.id 
                  ? "text-text-layer-primary bg-surface-elevated border-l-foreground"
                  : "text-text-layer-muted hover:text-text-layer-primary hover:bg-surface-elevated border-l-transparent"
              )}
              title={tab.label}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-[9px] font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className={cn("flex items-center justify-between px-4 py-3 border-b shrink-0", utilityPane.border)}>
            <h2 className={cn("font-semibold", utilityPane.text)}>
              {activeTab === 'dialer' && 'Quick Dial'}
              {activeTab === 'tasks' && 'Tasks'}
              {activeTab === 'notes' && 'Notes'}
              {activeTab === 'quickstart' && (isQuickstartComplete && setupDismissed ? 'Activity' : 'Quick Start')}
              {activeTab === 'help' && 'Help'}
            </h2>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="text-text-layer-muted hover:bg-surface-elevated hover:text-text-layer-primary">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            {/* Dialer Tab - Full GlobalDialer embedded */}
            {activeTab === 'dialer' && (
              <div className="h-full">
                <GlobalDialer 
                  isModal={false}
                  showNavigation={false}
                  className="h-full"
                />
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="p-4 space-y-3">
                {tasks.length === 0 ? (
                  <div className="text-center py-8">
                    <ListTodo className="h-10 w-10 mx-auto text-text-layer-muted mb-3" />
                    <p className="text-text-layer-primary font-medium">All caught up!</p>
                    <p className="text-sm text-text-layer-muted">No urgent tasks right now</p>
                  </div>
                ) : (
                  tasks.map((task: any) => (
                    <div 
                      key={task.id}
                      className="p-3 rounded-lg bg-card border border-border hover:border-border-layer-strong transition-colors cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "p-2 rounded-full shrink-0",
                          task.type === 'missed_call' ? 'bg-red-500/10 text-red-500' :
                          task.type === 'sms' ? 'bg-surface-elevated text-text-layer-secondary' :
                          'bg-surface-elevated text-text-layer-secondary'
                        )}>
                          {task.type === 'missed_call' ? <PhoneMissed className="h-4 w-4" /> :
                           task.type === 'sms' ? <MessageSquare className="h-4 w-4" /> :
                           <Volume2 className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-layer-primary">{task.from}</p>
                          <p className="text-xs text-text-layer-muted truncate">{task.summary}</p>
                        </div>
                        <Badge className="shrink-0 bg-orange-500/10 text-orange-400 border-0 text-xs">
                          {formatOverdue(task.age)}
                        </Badge>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" className="h-7 text-xs flex-1">
                          {task.type === 'missed_call' ? 'Call Back' : 'Reply'}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-border text-text-layer-primary hover:bg-surface-elevated">
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <Textarea
                    placeholder="Write a quick note..."
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    className="min-h-[80px] resize-none bg-input border-border text-text-layer-primary placeholder:text-text-layer-muted"
                  />
                  <Button 
                    onClick={addNote} 
                    disabled={!currentNote.trim() || createNoteMutation.isPending} 
                    className="w-full"
                  >
                    {createNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                  </Button>
                </div>

                {notesLoading ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-text-layer-muted">Loading notes...</p>
                  </div>
                ) : notesError ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-red-400">Failed to load notes</p>
                  </div>
                ) : sortedNotes.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-text-layer-muted font-medium">Your Notes</p>
                    {sortedNotes.map((note) => (
                      <div 
                        key={note.id} 
                        className={cn(
                          "p-3 rounded-lg border transition-colors",
                          note.isPinned 
                            ? "bg-surface-elevated border-border-layer-strong"
                            : "bg-card border-border"
                        )}
                      >
                        <p className="text-sm text-text-layer-primary whitespace-pre-wrap">{note.content}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button 
                            onClick={() => togglePinNote(note.id)}
                            disabled={updateNoteMutation.isPending}
                            className={cn(
                              "text-xs flex items-center gap-1",
                              note.isPinned ? "text-text-layer-primary" : "text-text-layer-muted hover:text-text-layer-primary"
                            )}
                          >
                            <Pin className="h-3 w-3" />
                            {note.isPinned ? 'Pinned' : 'Pin'}
                          </button>
                          <button 
                            onClick={() => convertToTask(note.id)}
                            className="text-xs text-text-layer-muted hover:text-text-layer-primary flex items-center gap-1"
                          >
                            <ListTodo className="h-3 w-3" />
                            To Task
                          </button>
                          <button 
                            onClick={() => deleteNote(note.id)}
                            disabled={deleteNoteMutation.isPending}
                            className="text-xs text-text-layer-muted hover:text-red-400 flex items-center gap-1 ml-auto"
                          >
                            <Delete className="h-3 w-3" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-text-layer-muted">No notes yet. Write something above!</p>
                  </div>
                )}
              </div>
            )}

            {/* Quick Start Tab - Smart Logic */}
            {activeTab === 'quickstart' && (
              <div className="p-4 space-y-4">
                {/* State 1: Setup Complete + Dismissed = Activity View */}
                {(isQuickstartComplete && setupDismissed) ? (
                  <div className="space-y-4">
                    {/* Quick Actions */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="h-4 w-4 text-text-layer-secondary" />
                        <h3 className="font-medium text-text-layer-primary">Quick Actions</h3>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-border text-text-layer-primary hover:bg-surface-elevated"
                          onClick={() => setActiveTab('dialer')}
                        >
                          <Phone className="h-4 w-4 mr-1.5" />
                          New Call
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-border text-text-layer-primary hover:bg-surface-elevated"
                          onClick={() => {
                            if (onNewSms) {
                              setIsOpen(false);
                              onNewSms();
                            } else {
                              setIsOpen(false);
                              setLocation('/inbox');
                            }
                          }}
                        >
                          <MessageSquare className="h-4 w-4 mr-1.5" />
                          New SMS
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-border text-text-layer-primary hover:bg-surface-elevated"
                          onClick={() => {
                            if (onNewContact) {
                              setIsOpen(false);
                              onNewContact();
                            } else {
                              setIsOpen(false);
                              setLocation('/contacts');
                            }
                          }}
                        >
                          <Users className="h-4 w-4 mr-1.5" />
                          New Contact
                        </Button>
                      </div>
                    </div>

                    {/* Today Stats */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="h-4 w-4 text-text-layer-muted" />
                        <h3 className="font-medium text-text-layer-primary">Today</h3>
                      </div>
                      <div className="bg-card border border-border rounded-lg p-3">
                        <p className="text-sm text-text-layer-secondary">
                          {tasks.length > 0 ? `${tasks.length} items need attention` : 'All caught up!'}
                        </p>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    {tasks.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Clock className="h-4 w-4 text-text-layer-muted" />
                          <h3 className="font-medium text-text-layer-primary">Recent</h3>
                        </div>
                        <div className="space-y-2">
                          {tasks.slice(0, 3).map((task: any, index: number) => (
                            <div 
                              key={task.id || index}
                              className="flex items-center justify-between p-2 bg-card border border-border rounded-lg text-sm"
                            >
                              <div className="flex items-center gap-2">
                                {task.type === 'missed_call' ? (
                                  <PhoneMissed className="h-4 w-4 text-red-400" />
                                ) : task.type === 'sms' ? (
                                  <MessageSquare className="h-4 w-4 text-text-layer-secondary" />
                                ) : (
                                  <Volume2 className="h-4 w-4 text-purple-400" />
                                )}
                                <span className="text-text-layer-primary">{task.from || 'Unknown'}</span>
                              </div>
                              <span className="text-xs text-text-layer-muted">{formatOverdue(task.age || '')}</span>
                            </div>
                          ))}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full mt-2 text-xs text-text-layer-muted hover:bg-surface-elevated hover:text-text-layer-primary"
                          onClick={() => { setIsOpen(false); setLocation('/inbox'); }}
                        >
                          View All Activity
                          <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : isQuickstartComplete ? (
                  /* State 2: Setup Complete - Celebration */
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🎉</div>
                    <p className="text-lg font-semibold text-text-layer-primary">You're all set!</p>
                    <p className="text-sm text-text-layer-muted mt-2 mb-6">
                      Your phone system is ready to use.
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button 
                        variant="outline"
                        size="sm"
                        className="border-border text-text-layer-primary hover:bg-surface-elevated"
                        onClick={handleDismissSetup}
                      >
                        Dismiss
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-primary hover:bg-primary/90"
                        onClick={handleExploreFeatures}
                      >
                        Explore
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* State 3: Onboarding - Show Only Incomplete Items */
                  <>
                    {/* Progress Header with Ring */}
                    <div className="bg-card rounded-xl p-4 border border-border">
                      <div className="flex items-center gap-4">
                        {/* Animated Progress Ring */}
                        <div className="relative h-16 w-16 shrink-0">
                          <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
                            <circle
                              cx="32"
                              cy="32"
                              r="28"
                              fill="none"
                              stroke="#2A2B32"
                              strokeWidth="4"
                            />
                            <circle
                              cx="32"
                              cy="32"
                              r="28"
                              fill="none"
                              stroke="url(#progressGradient)"
                              strokeWidth="4"
                              strokeLinecap="round"
                              strokeDasharray={`${(progressPercent / 100) * 176} 176`}
                              className="transition-all duration-700 ease-out"
                            />
                            <defs>
                              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#D6DAE4" />
                                <stop offset="100%" stopColor="#8B91A1" />
                              </linearGradient>
                            </defs>
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-bold text-text-layer-primary">{progressPercent}%</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-text-layer-primary text-base">Setup Progress</h3>
                          <p className="text-sm text-text-layer-muted mt-0.5">
                            {completedItems} of {totalItems} steps complete
                          </p>
                          {remainingItems <= 3 && remainingItems > 0 && (
                            <p className="text-xs text-text-layer-secondary mt-1 flex items-center gap-1">
                              <Sparkles className="h-3 w-3" />
                              Almost there!
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Incomplete Items with Stagger Animation */}
                    <div className="space-y-2">
                      {quickstartSections.flatMap(section => 
                        section.items
                          .filter(item => !item.completed)
                      ).map((item, index) => (
                        <div
                          key={item.id}
                          className="group p-4 bg-card hover:bg-surface-elevated rounded-xl border border-border hover:border-border-layer-strong transition-all duration-200"
                          style={{ 
                            animationDelay: `${index * 50}ms`,
                            animation: 'fadeInUp 0.3s ease-out forwards'
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              {/* Step indicator */}
                              <div className="h-8 w-8 rounded-full bg-input group-hover:bg-surface-elevated border-2 border-border group-hover:border-border-layer-strong flex items-center justify-center shrink-0 transition-all duration-200">
                                <span className="text-xs font-semibold text-text-layer-muted group-hover:text-text-layer-primary transition-colors">
                                  {index + 1}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0 pt-0.5">
                                <p className="text-sm font-medium text-text-layer-primary group-hover:text-white transition-colors">
                                  {item.title}
                                </p>
                                {item.description && (
                                  <p className="text-xs text-text-layer-muted mt-1 leading-relaxed">
                                    {item.description}
                                  </p>
                                )}
                                {item.actionLabel && (
                                  <Button 
                                    size="sm" 
                                    className="mt-3 h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/30 transition-all"
                                    onClick={() => handleAction(item)}
                                  >
                                    {item.actionLabel}
                                    <ArrowRight className="h-3 w-3 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            {item.skipLabel && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 text-xs text-text-layer-muted hover:text-text-layer-primary hover:bg-surface-elevated shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handleSkip(item)}
                              >
                                {item.skipLabel}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Completed Summary with Animation */}
                    {completedItems > 0 && (
                      <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <div className="flex items-center gap-2 text-sm">
                          <div className="h-6 w-6 rounded-full bg-green-500/20 flex items-center justify-center">
                            <CheckCircle2 className="h-4 w-4 text-green-400" />
                          </div>
                          <span className="text-green-400 font-medium">{completedItems} completed</span>
                        </div>
                        <div className="flex -space-x-1">
                          {Array.from({ length: Math.min(completedItems, 4) }).map((_, i) => (
                            <div key={i} className="h-5 w-5 rounded-full bg-green-500/30 border-2 border-background flex items-center justify-center">
                              <Check className="h-2.5 w-2.5 text-green-400" />
                            </div>
                          ))}
                          {completedItems > 4 && (
                            <div className="h-5 w-5 rounded-full bg-green-500/20 border-2 border-background flex items-center justify-center">
                              <span className="text-[8px] text-green-400">+{completedItems - 4}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* CSS for animation */}
                    <style>{`
                      @keyframes fadeInUp {
                        from {
                          opacity: 0;
                          transform: translateY(8px);
                        }
                        to {
                          opacity: 1;
                          transform: translateY(0);
                        }
                      }
                    `}</style>
                  </>
                )}
              </div>
            )}

            {/* Help Tab */}
            {activeTab === 'help' && (
              <div className="p-4 space-y-4">
                <div className="text-center mb-6">
                  <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 border", utilityPane.elevated, utilityPane.border)}>
                    <HelpCircle className="h-6 w-6 text-text-layer-secondary" />
                  </div>
                  <h3 className="font-semibold text-text-layer-primary">How can we help?</h3>
                  <p className="text-xs text-text-layer-muted mt-1">Find answers and get support</p>
                </div>

                <div className="space-y-2">
                  <a 
                    href="https://help.limephone.io" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn("flex items-center gap-3 p-3 rounded-lg border transition-colors group", utilityPane.elevated, utilityPane.elevatedHover, utilityPane.border)}
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", utilityPane.surface)}>
                      <BookOpen className="h-5 w-5 text-text-layer-muted group-hover:text-text-layer-primary transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-layer-primary">Help Center</p>
                      <p className="text-xs text-text-layer-muted">Browse articles & guides</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-text-layer-muted" />
                  </a>

                  <a 
                    href="/helpdesk" 
                    className={cn("flex items-center gap-3 p-3 rounded-lg border transition-colors group", utilityPane.elevated, utilityPane.elevatedHover, utilityPane.border)}
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", utilityPane.surface)}>
                      <MessageCircle className="h-5 w-5 text-text-layer-muted group-hover:text-text-layer-primary transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-layer-primary">Support</p>
                      <p className="text-xs text-text-layer-muted">View & create tickets</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-text-layer-muted" />
                  </a>

                  <a 
                    href="tel:+17623165814" 
                    className={cn("flex items-center gap-3 p-3 rounded-lg border transition-colors group", utilityPane.elevated, utilityPane.elevatedHover, utilityPane.border)}
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", utilityPane.surface)}>
                      <Phone className="h-5 w-5 text-text-layer-muted group-hover:text-text-layer-primary transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-layer-primary">Phone & Text Support</p>
                      <p className="text-xs text-text-layer-muted">+1 (762) 316-5814</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-text-layer-muted" />
                  </a>

                  <button 
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).HubSpotConversations) {
                        (window as any).HubSpotConversations.widget.open();
                      }
                    }}
                    className={cn("w-full flex items-center gap-3 p-3 rounded-lg border transition-colors group text-left", utilityPane.elevated, utilityPane.elevatedHover, utilityPane.border)}
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", utilityPane.surface)}>
                      <MessageSquare className="h-5 w-5 text-text-layer-muted group-hover:text-text-layer-primary transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-layer-primary">Live Chat</p>
                      <p className="text-xs text-text-layer-muted">Chat with our team</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-text-layer-muted" />
                  </button>

                  <a 
                    href="mailto:support@limephone.io" 
                    className={cn("flex items-center gap-3 p-3 rounded-lg border transition-colors group", utilityPane.elevated, utilityPane.elevatedHover, utilityPane.border)}
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", utilityPane.surface)}>
                      <Mail className="h-5 w-5 text-text-layer-muted group-hover:text-text-layer-primary transition-colors" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-layer-primary">Email Support</p>
                      <p className="text-xs text-text-layer-muted">support@limephone.io</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-text-layer-muted" />
                  </a>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-text-layer-muted text-center mb-3">Keyboard shortcuts</p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-text-layer-muted">Quick dial</span>
                      <kbd className="px-2 py-1 bg-surface-elevated rounded text-text-layer-secondary">D</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-layer-muted">New message</span>
                      <kbd className="px-2 py-1 bg-surface-elevated rounded text-text-layer-secondary">M</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-layer-muted">Search</span>
                      <kbd className="px-2 py-1 bg-surface-elevated rounded text-text-layer-secondary">⌘K</kbd>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <Button
                    onClick={() => {
                      resetTours();
                      setIsOpen(false);
                      setTimeout(() => startTour('main'), 300);
                    }}
                    variant="outline"
                    className="w-full border-border text-text-layer-primary hover:bg-surface-elevated"
                  >
                    <Rocket className="h-4 w-4 mr-2" />
                    Take the tour
                  </Button>
                  {tourState.hasCompletedMainTour && (
                    <p className="text-xs text-text-layer-muted text-center mt-2">
                      ✓ Tour completed
                    </p>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Backdrop when open */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Invite Team Member Popup */}
      <Dialog open={showInvitePopup} onOpenChange={setShowInvitePopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new team member to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowInvitePopup(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => inviteMutation.mutate({ email: inviteEmail, role: inviteRole })}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
            >
              {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
